import {
    isEmpty,
    toUSE164,
    zodLegalFirstNameValidation,
    zodLegalLastNameValidation,
} from '@fruitful/fruitils';
import { AnalyticsUserEvent } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { router } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    Form,
    Icon,
    ScreenContainer,
    SelectSingle,
    Sheet,
    Spinner,
    Stack,
    TrashIcon,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import {
    formatWithMask,
    PHONE_NUMBER_MASK,
    VALID_PHONE_NUMBER_MASK,
} from '../../common/utils/maskUtils';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException, track } from '../../logger/Logger';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from '../../member-profile/components/SuccessToast';
import useDeleteTrustedContact from '../hooks/useDeleteTrustedContact';
import useGetTrustedContact from '../hooks/useGetTrustedContact';
import useUpsertTrustedContact from '../hooks/useUpsertTrustedContact';

const trustedContactSchema = z.object({
    firstName: zodLegalFirstNameValidation,
    lastName: zodLegalLastNameValidation,
    email: z.string().email('Please enter a valid email address.'),
    phoneNumber: z
        .string()
        .min(10, 'Please enter a valid phone number.')
        .regex(VALID_PHONE_NUMBER_MASK, 'Please enter a valid phone number.'),
});

type TrustedContactSchema = z.infer<typeof trustedContactSchema>;

export const TrustedContactForm = ({
    userId,
    hasBackButton = true,
    submitButtonText = 'Save',
    onSkip,
    onSuccess,
    onShowForm,
    isInsideModal = false,
    isFormShowing = false,
}: {
    userId: string;
    hasBackButton?: boolean;
    submitButtonText?: string;
    onSkip: () => void;
    onSuccess: () => void;
    /** Callback for parent to handle when contact form becomes visible (e.g. to maintain parent state for controlling this component) */
    onShowForm?: () => void;
    isInsideModal?: boolean;
    /** Allows parent to control whether the contact form is visible (as opposed to the initial screen) */
    isFormShowing?: boolean;
}) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Profile/TrustedContact');

    const toast = useToastController();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForm, setShowFormState] = useState(isFormShowing);
    const [isDeleteSheetOpen, setIsDeleteSheetOpen] = useState(false);

    const { data: trustedContact, isLoading } = useGetTrustedContact({
        userId,
    });

    const { mutateAsync: upsertTrustedContact, isPending: isUpserting } =
        useUpsertTrustedContact();

    const { mutateAsync: deleteTrustedContact, isPending: isDeleting } =
        useDeleteTrustedContact();

    const {
        handleSubmit,
        formState: { isValid, errors },
        register,
        control,
        reset,
        setFocus,
    } = useForm<TrustedContactSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(trustedContactSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
        },
    });

    // Helper function to maintain internal state and handle callback everywhere that we update `showForm`
    const setShowForm = useCallback(
        (show: boolean) => {
            setShowFormState(show);
            if (show) {
                onShowForm?.();
            }
        },
        [onShowForm],
    );

    useEffect(() => {
        // Maintain state when provided prop changes (e.g. being controlled by parent)
        setShowForm(isFormShowing);
    }, [isFormShowing]);

    useEffect(() => {
        if (!isLoading) {
            /* Convert E164 phone number to display format for the form */
            const phoneNumberForDisplay =
                trustedContact?.phoneNumber != null
                    ? formatWithMask(
                          PHONE_NUMBER_MASK,
                          /**
                           * If we have a phone number, we know it's in US E164 format
                           * Thus, the last 10 digits are the phone number we want
                           * to mask and render
                           */
                          trustedContact.phoneNumber.slice(2),
                      ).masked
                    : '';

            reset({
                firstName: trustedContact?.firstName ?? '',
                lastName: trustedContact?.lastName ?? '',
                email: trustedContact?.email ?? '',
                phoneNumber: phoneNumberForDisplay,
            });
            if (!isEmpty(trustedContact)) {
                setShowForm(true);
            }
        }
    }, [trustedContact, isLoading, reset]);

    const onSubmit: SubmitHandler<TrustedContactSchema> = useCallback(
        async (data) => {
            setIsSubmitting(true);
            try {
                const phoneNumberE164 = toUSE164(data.phoneNumber);

                await upsertTrustedContact({
                    urlParams: { userId },
                    body: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        /* Form validation means we only produce valid phone numbers */
                        phoneNumber: phoneNumberE164 as string,
                    },
                });

                track(AnalyticsUserEvent.TRUSTED_CONTACT_SAVE);

                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });

                onSuccess();
            } catch (error) {
                captureException(error);
                toast.show('Something went wrong!', { duration: 2000 });
            } finally {
                setIsSubmitting(false);
            }
        },
        [upsertTrustedContact, toast, userId],
    );

    const handleDelete = useCallback(async () => {
        setIsDeleteSheetOpen(false);
        try {
            await deleteTrustedContact({ urlParams: { userId } });
            track(AnalyticsUserEvent.TRUSTED_CONTACT_DELETE);
            toast.show('Trusted contact deleted successfully', {
                duration: 2000,
            });
            router.navigate('/profile/financial-profile');
        } catch (error) {
            captureException(error);
            toast.show('Failed to delete trusted contact', { duration: 2000 });
        }
    }, [deleteTrustedContact, toast, userId]);

    const isFormSubmitting =
        isSubmitting || isUpserting || isLoading || isDeleting;

    const getContent = () => {
        return isFormSubmitting ? (
            <Spinner />
        ) : !showForm ? (
            <YStack gap={24} justifyContent="flex-start">
                <BodyLargeRegular>
                    Would you like to add a trusted contact?
                </BodyLargeRegular>
                <BodyMediumRegular color="$gray1000">
                    If we notice any unusual activity with your investment
                    accounts and can’t reach you, we’ll contact your trusted
                    contact to help confirm your info.
                </BodyMediumRegular>
                <SelectSingle
                    options={[
                        { title: 'Add trusted contact' },
                        { title: 'Skip for now' },
                    ]}
                    onSelect={(index) => {
                        if (index === 0) {
                            setShowForm(true);
                        } else {
                            onSkip();
                        }
                    }}
                />
            </YStack>
        ) : (
            <YStack gap={24} justifyContent="flex-start">
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <YStack gap={16}>
                        <XStack gap={12}>
                            <View flex={1}>
                                <ControlledInput
                                    name="firstName"
                                    label="First name"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    autoComplete="name-given"
                                    textContentType="name"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('lastName');
                                    }}
                                />
                            </View>
                            <View flex={1}>
                                <ControlledInput
                                    name="lastName"
                                    label="Last name"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    autoComplete="name-family"
                                    textContentType="familyName"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('email');
                                    }}
                                />
                            </View>
                        </XStack>

                        <ControlledInput
                            name="email"
                            label="Email"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="none"
                            autoComplete="email"
                            textContentType="emailAddress"
                            keyboardType="email-address"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                setFocus('phoneNumber');
                            }}
                        />

                        <ControlledInput
                            name="phoneNumber"
                            label="Phone number"
                            control={control}
                            register={register}
                            errors={errors}
                            formatValueOnChange={(_, value) => {
                                const { masked } = formatWithMask(
                                    PHONE_NUMBER_MASK,
                                    value,
                                );
                                return masked;
                            }}
                            autoComplete="tel-national"
                            inputMode="tel"
                            keyboardType="numbers-and-punctuation"
                        />
                    </YStack>
                </Form>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text={submitButtonText}
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid || isFormSubmitting}
                    marginTop="auto"
                />
            </YStack>
        );
    };

    if (isInsideModal) {
        return <View flex={1}>{getContent()}</View>;
    }

    return (
        <ScreenContainer
            title="Trusted contact"
            hasBackButton={hasBackButton}
            rightAction={
                trustedContact && showForm ? (
                    <Stack
                        padding={8}
                        borderRadius={16}
                        cursor="pointer"
                        onPress={() => {
                            setIsDeleteSheetOpen(true);
                        }}
                    >
                        <Icon icon={TrashIcon} size={16} color="$red1100" />
                    </Stack>
                ) : undefined
            }
        >
            {getContent()}
            <DeleteTrustedContactSheet
                isOpen={isDeleteSheetOpen}
                onClose={() => {
                    setIsDeleteSheetOpen(false);
                }}
                onDelete={handleDelete}
            />
        </ScreenContainer>
    );
};

interface DeleteTrustedContactSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onDelete: () => void;
}

const DeleteTrustedContactSheet = ({
    isOpen,
    onClose,
    onDelete,
}: DeleteTrustedContactSheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Delete trusted contact?"
            showCloseButton={false}
        >
            <YStack paddingTop={24} paddingHorizontal={4} gap={8}>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    tone="danger"
                    text="Delete"
                    onPress={onDelete}
                />
                <Button
                    variant={ButtonVariant.TERTIARY}
                    text="Cancel"
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};
